import {
  forwardRef,
  useId,
  useState,
  useRef,
  useEffect,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

export const SelectRoot = SelectPrimitive.Root
export const SelectGroup = SelectPrimitive.Group
export const SelectValue = SelectPrimitive.Value

export const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { error?: boolean }
>(({ className, children, error, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-body text-gray-900 shadow-sm transition',
      'focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500',
      'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500',
      'data-[placeholder]:text-gray-400',
      error ? 'border-red-400 focus:border-red-500 focus:ring-red-400/30' : 'border-gray-300',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 text-gray-500" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = 'SelectTrigger'

export const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      className={cn(
        'z-popover overflow-hidden rounded-md border border-gray-200 bg-white shadow-popover animate-zoom-in-95',
        position === 'popper' && 'w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)]',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.Viewport className="p-1">
        {props.children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = 'SelectContent'

export const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded py-1.5 pl-7 pr-2 text-body text-gray-700 outline-none',
      'focus:bg-gray-100 focus:text-gray-900',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-1.5 flex h-4 w-4 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3.5 w-3.5 text-brand-700" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = 'SelectItem'

export const SelectSeparator = forwardRef<
  ElementRef<typeof SelectPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn('my-1 h-px bg-gray-200', className)} {...props} />
))
SelectSeparator.displayName = 'SelectSeparator'

export const SelectLabel = forwardRef<
  ElementRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-caption font-semibold uppercase tracking-wide text-gray-500', className)}
    {...props}
  />
))
SelectLabel.displayName = 'SelectLabel'

/**
 * High-Level-Wrapper fuer den haeufigsten Fall: einfache Options-Liste + Label.
 */
interface SelectOption<T extends string = string> {
  value: T
  label: ReactNode
  disabled?: boolean
}

interface SelectProps<T extends string = string> {
  value?: T
  defaultValue?: T
  onValueChange?: (value: T) => void
  options: SelectOption<T>[]
  placeholder?: string
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  disabled?: boolean
  required?: boolean
  wrapperClassName?: string
  triggerClassName?: string
  id?: string
  searchable?: boolean
  searchPlaceholder?: string
}

export function Select<T extends string = string>({
  value,
  defaultValue,
  onValueChange,
  options,
  placeholder = 'Bitte wählen …',
  label,
  hint,
  error,
  disabled,
  required,
  wrapperClassName,
  triggerClassName,
  id,
  searchable = false,
  searchPlaceholder = 'Suchen …',
}: SelectProps<T>) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hasError = Boolean(error)
  const SENTINEL = '__select_empty__'
  const internalValue = value === '' ? SENTINEL : value
  const internalDefault = defaultValue === '' ? SENTINEL : defaultValue
  const handleChange = (v: string) => {
    if (!onValueChange) return
    onValueChange((v === SENTINEL ? '' : v) as T)
  }

  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open && searchable) {
      // Small delay to let Radix finish its own focus management
      const t = setTimeout(() => searchInputRef.current?.focus(), 10)
      return () => clearTimeout(t)
    } else if (!open) {
      setSearchQuery('')
    }
  }, [open, searchable])

  const filteredOptions = searchable && searchQuery.trim()
    ? options.filter(opt =>
        String(opt.label).toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options

  return (
    <div className={cn('flex flex-col gap-1', wrapperClassName)}>
      {label ? (
        <label htmlFor={fieldId} className="text-small font-medium text-gray-700">
          {label}
          {required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </label>
      ) : null}
      <SelectRoot
        value={internalValue}
        defaultValue={internalDefault}
        onValueChange={handleChange}
        disabled={disabled}
        required={required}
        open={searchable ? open : undefined}
        onOpenChange={searchable ? setOpen : undefined}
      >
        <SelectTrigger id={fieldId} error={hasError} className={triggerClassName}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        {searchable ? (
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              position="popper"
              className={cn(
                'z-popover rounded-md border border-gray-200 bg-white shadow-popover animate-zoom-in-95',
                'w-[var(--radix-select-trigger-width)]',
              )}
            >
              <div className="p-1.5 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                  <input
                    ref={searchInputRef}
                    className="w-full rounded border border-gray-200 pl-7 pr-2 py-1.5 text-body outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              </div>
              <SelectPrimitive.Viewport
                className="p-1 overflow-y-auto"
                style={{ maxHeight: 'min(var(--radix-select-content-available-height, 300px), 300px)' }}
              >
                {filteredOptions.map((opt) => {
                  const itemValue = opt.value === '' ? SENTINEL : opt.value
                  return (
                    <SelectItem key={opt.value} value={itemValue} disabled={opt.disabled}>
                      {opt.label}
                    </SelectItem>
                  )
                })}
                {filteredOptions.length === 0 && (
                  <div className="py-3 text-center text-small text-gray-400">Keine Ergebnisse</div>
                )}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        ) : (
          <SelectContent>
            {options.map((opt) => {
              const itemValue = opt.value === '' ? SENTINEL : opt.value
              return (
                <SelectItem key={opt.value} value={itemValue} disabled={opt.disabled}>
                  {opt.label}
                </SelectItem>
              )
            })}
          </SelectContent>
        )}
      </SelectRoot>
      {error ? (
        <span className="text-caption text-red-600">{error}</span>
      ) : hint ? (
        <span className="text-caption text-gray-500">{hint}</span>
      ) : null}
    </div>
  )
}
